/**
 * Chat Service for handling all AI chat-related API calls
 */

import { defaultErrorHandler as commonErrorHandler, createHeaders as createCommonHeaders, getApiUrls, getEnvConfig } from './serviceConfig';
import { createStreamProcessor, processStream } from './streamProcessors';

// Default stream timeout for all modes
const DEFAULT_STREAM_TIMEOUT = 30000; // 30 seconds

/**
 * Creates a configured chat service
 * @param {Object} config - Configuration options
 * @param {string} config.baseUrl - Base URL for the API
 * @param {Function} config.getUserId - Function to get the current user ID
 * @param {string} config.defaultModel - Default model to use if not specified
 * @param {string} config.environment - Environment ('development' or 'production')
 * @param {Object} config.headers - Additional headers to include in all requests
 * @param {Function} config.onError - Global error handler
 * @param {Function} config.getAuthToken - Function to get the latest auth token (preferred)
 * @param {string} config.authToken - Static authentication token for API requests (not recommended)
 * @param {number} config.streamTimeout - Timeout for streaming requests in milliseconds (default: 30000)
 * @returns {Object} The chat service API
 */
export function createChatService(config = {}) {
  // Get environment-specific defaults
  const envConfig = getEnvConfig();

  // Configure service
  const BASE_URL = config.baseUrl || `${envConfig.baseUrl}${envConfig.apiPrefix}`;
  const DEFAULT_HEADERS = config.headers || {};
  const STREAM_TIMEOUT = config.streamTimeout || DEFAULT_STREAM_TIMEOUT;
  const defaultErrorHandler = config.onError || ((error) => commonErrorHandler('Chat service', error));
  // Use getAuthToken function if provided, or use static token as fallback
  const getAuthToken = config.getAuthToken || (() => config.authToken);

  /**
   * Helper to create request headers
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Object} Headers object
   */
  const createHeaders = (additionalHeaders = {}) => {
    // Only pass custom auth getter if one was provided to the service
    const customAuthGetter = config.getAuthToken ? getAuthToken : null;
    return createCommonHeaders(
      {
        ...DEFAULT_HEADERS,
        ...additionalHeaders,
      },
      customAuthGetter
    );
  };

  /**
   * Helper to handle API errors
   * @param {Response} response - Fetch Response object
   * @param {string} errorPrefix - Prefix for error message
   * @returns {Promise} JSON response or throws error
   */
  const handleResponse = async (response, errorPrefix = 'API error') => {
    if (!response.ok) {
      let errorMessage = `${errorPrefix}: ${response.status}`;

      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = `${errorPrefix}: ${errorData.message}`;
        }
      } catch {
        // If we can't parse the error JSON, just use the status code
      }

      const error = new Error(errorMessage);
      error.statusCode = response.status;
      throw error;
    }

    return response.json();
  };

  return {
    /**
     * Create a new conversation
     *
     * @param {Object} options - Optional configuration
     * @param {string} options.systemPrompt - Custom system prompt to override default
     * @param {string} options.model - Custom model to use for this conversation
     * @param {Object} options.context - Additional context for the conversation
     * @param {string} options.trigger - Trigger type (e.g., 'DIRECT', 'AUTO')
     * @param {string} options.mode - Chat mode ('study' or 'practice')
     * @param {Object} options.llmConfig - LLM configuration with model, temperature, maxTokens
     * @param {Object} options.features - Optional features configuration (e.g., { enableExternalSearch: true })
     * @returns {Promise<Object>} New conversation object with conversationId
     */
    createConversation: async (options = {}) => {
      try {
        // Determine the chat mode (default to 'study')
        const mode = options.mode || 'study';

        const requestBody = {
          status: 'ACTIVE',
          trigger: options.trigger || 'DIRECT',
        };

        // Include systemPrompt if it exists
        if (options.systemPrompt) {
          requestBody.overrideSystemPrompt = options.systemPrompt;
        }

        // Include LLM configuration if provided
        if (options.llmConfig) {
          requestBody.overrideLLMConfig = options.llmConfig;
        }
        // If no llmConfig but model is provided, create a simple llmConfig
        else if (options.model) {
          requestBody.overrideLLMConfig = {
            model: options.model,
          };
        }

        // Include additional context if provided
        if (options.context) {
          requestBody.context = options.context;
        }

        if (options.activityInstanceId) {
          requestBody.activityInstanceId = options.activityInstanceId;
        }

        // Include features configuration if provided
        if (options.features) {
          requestBody.features = options.features;
        }

        const urls = getApiUrls();
        const response = await fetch(urls.chat.conversations(mode), {
          method: 'POST',
          headers: createHeaders(),
          body: Object.keys(requestBody).length > 0 ? JSON.stringify(requestBody) : undefined,
        });

        const data = await handleResponse(response, 'Error creating conversation');

        // Add mode to the returned conversation object for tracking
        return { ...data, mode };
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Get all conversations for the current user
     *
     * @param {Object} options - Optional configuration
     * @param {number} options.limit - Maximum number of conversations to return
     * @param {number} options.offset - Offset for pagination
     * @returns {Promise<Object>} Response object with conversations array and pagination metadata
     */
    getAllConversations: async (options = {}) => {
      try {
        const queryParams = new URLSearchParams();

        if (options.limit) {
          queryParams.append('limit', options.limit);
        }

        if (options.offset) {
          queryParams.append('offset', options.offset);
        }

        // Add default parameters as per API documentation
        queryParams.append('sortBy', 'updatedAt');
        queryParams.append('sortOrder', 'desc');

        const queryString = queryParams.toString();
        const endpoint = `${BASE_URL}/chat${queryString ? `?${queryString}` : ''}`;

        const response = await fetch(endpoint, {
          headers: createHeaders(),
        });

        const data = await handleResponse(response, 'Error fetching conversations');

        // Return the full response object with conversations and pagination metadata
        return data;
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Get a single conversation with all its messages
     *
     * @param {string} conversationId - ID of the conversation to retrieve
     * @param {string} mode - Chat mode ('study', 'practice', or 'teacher-tools') - defaults to 'study'
     * @returns {Promise<Object>} Conversation object with messages (and canvas for teacher-tools)
     */
    getConversation: async (conversationId, mode = 'study') => {
      try {
        const urls = getApiUrls();
        const response = await fetch(urls.chat.conversation(conversationId, mode), {
          method: 'GET',
          headers: createHeaders(),
        });

        return handleResponse(response, 'Error fetching conversation');
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Send a message to a conversation and stream the response
     *
     * @param {string} conversationId - ID of the existing conversation
     * @param {string} message - User message to send
     * @param {Object} options - Optional configuration
     * @param {string} options.systemPrompt - Custom system prompt to use for this message
     * @param {string} options.model - Custom model to use for this message
     * @param {number} options.temperature - Temperature setting for the model
     * @param {number} options.maxTokens - Maximum tokens for the response
     * @param {Object} options.llmConfig - Complete LLM configuration (model, temperature, maxTokens)
     * @param {Object} options.courseId - Optional course ID for context
     * @param {Object} options.unitId - Optional unit ID for context
     * @param {string} options.mode - Chat mode ('study', 'practice', or 'teacher-tools')
     * @param {function} onChunk - Callback for each chunk of the response
     * @param {function} onComplete - Callback when streaming is complete
     * @param {function} onError - Callback for error handling
     * @param {File} imageFile - Optional image file to upload with the message
     * @param {function} onCanvasDelta - Optional callback for canvas updates (teacher-tools)
     * @param {function} onMessageComplete - Optional callback when message ID is received from backend
     * @returns {Object} Object containing an abort method to stop the stream
     */
    streamChatResponseForConversation: async (
      conversationId,
      message,
      options = {},
      onChunk,
      onComplete,
      onError,
      imageFile = null,
      onCanvasDelta = null,
      onMessageComplete = null
    ) => {
      // Create an AbortController to allow stopping the stream
      const abortController = new AbortController();
      let reader = null;
      let processor = null;

      // Helper function to build the request body
      const buildRequestBody = (message, imageFile, options) => {
        const formData = new FormData();
        formData.append('messageContent', message || '');

        if (imageFile) {
          formData.append('images', imageFile);
        }

        if (options.courseId) {
          formData.append('courseId', options.courseId);
        }

        if (options.unitId) {
          formData.append('unitId', options.unitId);
        }

        if (options.studentResponse !== undefined && options.studentResponse !== null) {
          formData.append('studentResponse', options.studentResponse);
        }

        return formData;
      };

      try {
        // Determine the chat mode (default to 'study')
        const mode = options.mode || 'study';

        // Build headers (don't set Content-Type for FormData)
        const headers = createHeaders({
          ...options.headers,
          Accept: 'text/event-stream',
        });
        delete headers['Content-Type'];

        // Make the request
        const response = await fetch(`${BASE_URL}/chat/${mode}/${conversationId}`, {
          method: 'POST',
          headers: headers,
          body: buildRequestBody(message, imageFile, options),
          signal: abortController.signal,
        });

        if (!response.ok) {
          const error = new Error(`Error sending message: ${response.status}`);
          error.statusCode = response.status;
          throw error;
        }

        if (!response.body) {
          throw new Error('ReadableStream not supported in this browser.');
        }

        // Create the appropriate processor based on mode
        processor = createStreamProcessor(mode);
        reader = response.body.getReader();

        // Process the stream with timeout configuration
        await processStream(
          reader,
          processor,
          {
            onChunk,
            onComplete,
            onError,
            onCanvasDelta,
            onMessageComplete,
          },
          {
            timeout: STREAM_TIMEOUT,
          }
        );
      } catch (error) {
        // Handle AbortError specially
        if (error.name === 'AbortError') {
          return {
            abort: () => {},
          };
        }

        // Call the error handler
        if (typeof onError === 'function') {
          onError(error);
        }

        // Call the global error handler
        defaultErrorHandler(error);

        return {
          abort: () => {},
        };
      }

      // Return an object with an abort method to stop the stream
      return {
        abort: () => {
          abortController.abort();
          if (reader) {
            try {
              reader.cancel();
            } catch (e) {
              console.warn('Error cancelling reader:', e);
            }
          }
        },
      };
    },

    /**
     * Get text-to-speech audio for any text
     * @param {string} text - The text to convert to speech
     * @returns {Promise<Blob>} Audio blob
     */
    getTextAudio: async (text) => {
      const requestBody = {
        text: text,
      };

      try {
        const response = await fetch(`${BASE_URL}/chat/tts`, {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('TTS service not found');
          } else if (response.status === 403) {
            throw new Error('Access denied');
          } else if (response.status === 400) {
            const errorText = await response.text();
            if (errorText.includes('empty')) {
              throw new Error('Text is empty');
            } else if (errorText.includes('5000')) {
              throw new Error('Text exceeds 5000 characters');
            }
            throw new Error('Invalid request');
          }
          throw new Error(`TTS failed: ${response.status}`);
        }

        return response.blob();
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Update canvas content for a teacher-tools conversation
     * @param {string} conversationId - The conversation ID
     * @param {Object} data - Canvas update data
     * @param {string} data.content - Canvas content in markdown format
     * @param {string} [data.title] - Optional canvas title
     * @returns {Promise<Object>} Updated conversation object
     */
    updateCanvasContent: async (conversationId, data) => {
      try {
        const urls = getApiUrls();
        const response = await fetch(`${urls.chat.conversation(conversationId, 'teacher-tools')}/canvas`, {
          method: 'PATCH',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(data),
        });

        return handleResponse(response, 'Error updating canvas');
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Flag a message as problematic
     * @param {string} messageId - The ID of the message to flag
     * @param {string} reason - Single reason ID for flagging
     * @param {string|null} comment - Optional comment describing the issue
     * @returns {Promise<Object>} Response object with flag data
     */
    flagMessage: async (messageId, reason, comment = null) => {
      try {
        const requestBody = {
          messageId,
          reason,
        };

        if (comment) {
          requestBody.comment = comment;
        }

        const response = await fetch(`${BASE_URL}/chat/flag`, {
          method: 'POST',
          headers: createHeaders({
            'Content-Type': 'application/json',
          }),
          body: JSON.stringify(requestBody),
        });

        return handleResponse(response, 'Error flagging message');
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Get conversations for quiz jobs to calculate tutor usage statistics
     * Returns conversation data that must be cross-referenced with assessment answers
     *
     * @param {string[]} jobGuids - Array of quiz job GUIDs
     * @returns {Promise<Object>} Response with jobs array containing conversations
     * @example Response format:
     * {
     *   jobs: [{
     *     quizJobGuid: "job-guid-1",
     *     conversations: [{
     *       conversationId: "conv-uuid-1",
     *       activityGuid: "activity-uuid-1",
     *       messageCount: 5,
     *       progressStatus: "IN_PROGRESS" | "DONE" | null
     *     }]
     *   }]
     * }
     */
    getConversationsByJobs: async (jobGuids) => {
      try {
        if (!jobGuids || jobGuids.length === 0) {
          return { jobs: [] };
        }

        const urls = getApiUrls();
        const response = await fetch(urls.tutorStats.getConversationsByJobs(jobGuids), {
          method: 'GET',
          headers: createHeaders(),
        });

        return handleResponse(response, 'Error fetching tutor conversations');
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },
  };
}

// Default export with default configuration
export default createChatService();
